import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, Download, Loader2, X } from 'lucide-react';
import {
  JOB_LOG_SOURCE,
  Job,
  LogEvent,
  LogStreamReady,
  fetchJobLogText,
  openLogStream,
} from '../../services/JobMonitorService';
import { LogLineList, sourceLabel } from './LogLineList';
import styles from '../JobsPage.module.css';

interface LogViewerModalProps {
  job: Job;
  token: string;
  onClose: () => void;
}

// Held per source, not across all of them: a chatty service would otherwise
// push the job's own lines out of the buffer entirely.
const MAX_LINES_PER_SOURCE = 1_000;

// What the page draws. A run can print far more than this, so the rest is left
// to the downloaded file rather than being kept in the DOM.
const MAX_LINES_SHOWN = 1_000;

const ALL_SOURCES = 'all';

// Treat "within this far from the bottom" as following the tail.
const AT_BOTTOM_PX = 40;

type ConnectionState = 'connecting' | 'live' | 'reconnecting';

const append = (current: LogEvent[], entry: LogEvent): LogEvent[] => {
  const next = [...current, entry];
  if (next.length <= MAX_LINES_PER_SOURCE) return next;
  if (next.filter((line) => line.source === entry.source).length <= MAX_LINES_PER_SOURCE) return next;
  const oldest = next.findIndex((line) => line.source === entry.source);
  return [...next.slice(0, oldest), ...next.slice(oldest + 1)];
};

/**
 * Puts the sources back into one timeline. A finished run replays its own lines
 * as a block before the service's arrive, and a line the parser found no time on
 * (a traceback's later lines) keeps the time of the line it follows so it stays
 * with it.
 */
const inTimeOrder = (lines: LogEvent[]): LogEvent[] => {
  let carried = 0;
  const keyed = lines.map((line, index) => {
    carried = line.timestamp ? Date.parse(line.timestamp) : carried;
    return { line, at: carried, index };
  });
  keyed.sort((a, b) => a.at - b.at || a.index - b.index);
  return keyed.map((entry) => entry.line);
};

const save = (text: string, filename: string): void => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const LogViewerModal: React.FC<LogViewerModalProps> = ({ job, token, onClose }) => {
  const [lines, setLines] = useState<LogEvent[]>([]);
  const [sources, setSources] = useState<string[]>([JOB_LOG_SOURCE]);
  const [showing, setShowing] = useState<string>(ALL_SOURCES);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [lastHeartbeat, setLastHeartbeat] = useState<string | null>(null);
  const [following, setFollowing] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stream = openLogStream(job.job_id, token);

    stream.addEventListener('ready', (event) => {
      const ready = JSON.parse((event as MessageEvent).data) as LogStreamReady;
      setSources(ready.sources ?? [JOB_LOG_SOURCE]);
      setConnection('live');
    });
    stream.addEventListener('heartbeat', () => setLastHeartbeat(new Date().toLocaleTimeString()));
    stream.addEventListener('log', (event) => {
      const entry = JSON.parse((event as MessageEvent).data) as LogEvent;
      setLines((current) => append(current, entry));
    });
    // EventSource retries on its own; the server resumes from the last event id.
    stream.onerror = () => setConnection('reconnecting');

    return () => stream.close();
  }, [job.job_id, token]);

  const ordered = useMemo(
    () => inTimeOrder(showing === ALL_SOURCES ? lines : lines.filter((line) => line.source === showing)),
    [lines, showing],
  );
  const visible = useMemo(() => ordered.slice(-MAX_LINES_SHOWN), [ordered]);

  const counts = useMemo(() => {
    const tally = new Map<string, number>();
    for (const line of lines) tally.set(line.source, (tally.get(line.source) ?? 0) + 1);
    return tally;
  }, [lines]);

  // New lines only pull the view down while it is already at the tail, so
  // reading back through a run is not interrupted by the run still writing.
  useEffect(() => {
    if (following) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [visible, following]);

  const onScroll = () => {
    const body = bodyRef.current;
    if (body) setFollowing(body.scrollHeight - body.scrollTop - body.clientHeight < AT_BOTTOM_PX);
  };

  const download = async () => {
    setDownloading(true);
    setFailure(null);
    try {
      save(await fetchJobLogText(job.job_id, token), `${job.job_id}-logs.txt`);
    } catch (e) {
      setFailure((e as Error).message || 'Could not download the logs');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <div>
            <h3 className={styles.modalTitle}>Logs — {job.display_name}</h3>
            <span className={styles.connState}>
              {connection === 'live' ? 'Streaming' : connection === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
              {lastHeartbeat ? ` · heartbeat ${lastHeartbeat}` : ''}
            </span>
          </div>
          <div className={styles.modalHeadActions}>
            <button type="button" className={styles.secondaryBtn} disabled={downloading} onClick={download}>
              {downloading ? <Loader2 size={14} className={styles.spin} /> : <Download size={14} />} Download
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={onClose}>
              <X size={14} /> Close
            </button>
          </div>
        </div>

        {sources.length > 1 && (
          <div className={styles.sourceRow}>
            {[ALL_SOURCES, ...sources].map((source) => (
              <button
                key={source}
                type="button"
                className={`${styles.dayToggle} ${showing === source ? styles.dayToggleActive : ''}`}
                onClick={() => setShowing(source)}
              >
                {source === ALL_SOURCES ? `All (${lines.length})` : `${sourceLabel(source)} (${counts.get(source) ?? 0})`}
              </button>
            ))}
          </div>
        )}

        <div className={styles.modalBody} ref={bodyRef} onScroll={onScroll}>
          {failure && <div className={styles.errorBar}>{failure}</div>}
          {visible.length >= MAX_LINES_SHOWN && (
            <p className={styles.inlineNote}>
              Showing the last {MAX_LINES_SHOWN} lines. Download for the run's whole output.
            </p>
          )}
          <div className={styles.logStream}>
            {visible.length === 0 && <p className={styles.inlineNote}>No log output yet.</p>}
            <LogLineList lines={visible} showSource={sources.length > 1} />
            <div ref={bottomRef} />
          </div>
        </div>

        {!following && (
          <button type="button" className={styles.jumpToLatest} onClick={() => setFollowing(true)}>
            <ArrowDown size={13} /> Jump to latest
          </button>
        )}
      </div>
    </div>
  );
};
